import React, { createContext, useContext, useEffect } from 'react';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { useCSPNonce } from '@/hooks/useCSPNonce';
import { generateCSPString, SECURITY_HEADERS } from '@/config/security';
import { isDevelopment } from '@/utils/environment';

interface SecurityContextType {
  reportSecurityViolation: (violation: string, details?: any) => void;
  nonce: string;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurityContext must be used within a SecurityProvider');
  }
  return context;
};

interface SecurityProviderProps {
  children: React.ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const { reportSecurityViolation } = useSecurityMonitoring();
  const nonce = useCSPNonce();

  useEffect(() => {
    if (!nonce) return;

    const isDevMode = isDevelopment();

    // Only apply CSP via meta tags in development (production uses server headers)
    if (isDevMode) {
      console.log('Development mode: Applying relaxed CSP via meta tags');
      
      // Apply Content Security Policy with nonce for development
      const meta = document.createElement('meta');
      meta.httpEquiv = 'Content-Security-Policy';
      meta.content = generateCSPString(nonce, true); // Use dev CSP
      
      document.head.appendChild(meta);

      // Add security headers via meta tags where possible (dev only)
      Object.entries(SECURITY_HEADERS).forEach(([name, content]) => {
        // Skip frame-ancestors as it doesn't work in meta tags
        if (name === 'frame-ancestors') return;
        
        const metaTag = document.createElement('meta');
        metaTag.name = name;
        metaTag.content = content;
        document.head.appendChild(metaTag);
      });
    } else {
      console.log('Production mode: Using server-configured security headers');
    }

    // CSP violation reporting with enhanced logging
    const handleCSPViolation = (e: SecurityPolicyViolationEvent) => {
      // Only report violations in production to avoid dev noise
      if (!isDevMode) {
        reportSecurityViolation('CSP Violation', {
          violatedDirective: e.violatedDirective,
          blockedURI: e.blockedURI,
          lineNumber: e.lineNumber,
          sourceFile: e.sourceFile,
          originalPolicy: e.originalPolicy,
          disposition: e.disposition
        });
      }
    };

    document.addEventListener('securitypolicyviolation', handleCSPViolation);

    return () => {
      // Cleanup meta tags and event listeners on unmount
      if (isDevMode) {
        const metaTags = document.querySelectorAll('meta[http-equiv="Content-Security-Policy"], meta[name="X-Content-Type-Options"], meta[name="X-Frame-Options"], meta[name="Referrer-Policy"]');
        metaTags.forEach(tag => tag.remove());
      }
      document.removeEventListener('securitypolicyviolation', handleCSPViolation);
    };
  }, [reportSecurityViolation, nonce]);

  const contextValue: SecurityContextType = {
    reportSecurityViolation,
    nonce
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  );
};
